import {
  Accordion,
  Box,
  Card,
  Group,
  Switch,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { BellIcon, Sun, Translate } from "@phosphor-icons/react";
import { useFormik } from "formik";
import {
  usePreferences,
  useUpdatePreferences,
  type UserPreferences,
} from "@/api";

export default function PreferencesComponent() {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  const { data: userPreferences } = usePreferences();

  const { mutateAsync: updateUserPreferences } =
    useUpdatePreferences();


  const formik = useFormik<UserPreferences>({
    initialValues: {
      id: userPreferences?.id ?? 0,
      language: userPreferences?.language ?? "en",
      dark_theme: userPreferences?.dark_theme ?? colorScheme === "dark",
      notifications: {
        notifications: userPreferences?.notifications?.notifications ?? false,
        sound: userPreferences?.notifications?.sound ?? false,
        floating_notification: userPreferences?.notifications?.floating_notification ?? false,
        lock_screen_notification: userPreferences?.notifications?.lock_screen_notification ?? false,
      },
      user: userPreferences?.user ?? 0,
      uuid: userPreferences?.uuid ?? "",
      created_at: userPreferences?.created_at ?? "",
      updated_at: userPreferences?.updated_at ?? ""
    },
    enableReinitialize: true, 
    onSubmit: () => {
    },
  });


  return (
    <Card radius="md" p="xs" withBorder={false}>
      <Text fw={600} mb="md">
        Preferences
      </Text>

      {/* Notifications Accordion */}
      <Accordion
        variant="contained"
        // radius={"xl"}
        bg={
          colorScheme === "dark"
            ? theme.colors.neutral[9]
            : theme.colors.neutral[0]
        }
        styles={{
          item: {
            borderRadius: theme.radius.md,
            backgroundColor: "transparent",
          },
          control: {
            backgroundColor:
              colorScheme === "dark"
                ? theme.colors.neutral[9]
                : "#F1F7FE",
            borderBottom: `1px solid ${
              colorScheme === "dark"
                ? theme.colors.neutral[7]
                : theme.colors.gray[2]
            }`,
          },
          panel: {
            backgroundColor: "transparent",
          },
        }}
      >
        <Accordion.Item
          value="notifications"
        >
          <Accordion.Control>
            <Group>
              <BellIcon size={20} />
              <Text>Notifications</Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Box>
              <Card withBorder p={"sm"} mb={"sm"} mt="sm">
                <Group justify="space-between" mb="sm">
                  <Text>Notifications</Text>
                  <Switch
                    name="notifications.notifications"
                    checked={formik.values.notifications?.notifications}
                    onChange={async (e) => {
                      const updated = {
                        ...formik.values.notifications,
                        notifications: e.currentTarget.checked,
                      };
                      formik.setFieldValue(
                        "notifications.notifications",
                        e.currentTarget.checked
                      );
                      await updateUserPreferences({
                        data: {
                          ...formik.values,
                          notifications: updated,
                          
                        },
                      });
                    }}
                  />
                </Group>
              </Card>
              <Card withBorder p={"sm"} mb={"sm"}>
                <Group justify="space-between" mb="sm">
                  <Text>Sound</Text>
                  <Switch
                    name="notifications.sound"
                    checked={formik.values.notifications?.sound}
                    onChange={async (e) => {
                      const updated = {
                        ...formik.values.notifications,
                        sound: e.currentTarget.checked,
                      };
                      formik.setFieldValue("notifications", updated);
                      await updateUserPreferences({
                        data: {
                          ...formik.values,
                          notifications: updated,
                          language: formik.values.language,
                        },
                      });
                    }}
                  />
                </Group>
              </Card>
              <Card withBorder p={"sm"} mb={"sm"}>
                <Group justify="space-between" mb="sm">
                  <Text>Floating Notifications</Text>
                  <Switch
                    checked={formik.values.notifications?.floating_notification}
                    name="notifications.floating_notification"
                    onChange={async (e) => {
                      const updated = {
                        ...formik.values.notifications,
                        floating_notification: e.currentTarget.checked,
                      };
                      formik.setFieldValue("notifications", updated);
                      await updateUserPreferences({
                        data: {
                          ...formik.values,
                          notifications: updated,
                        },
                      });
                    }}
                  />
                </Group>
              </Card>
              <Card withBorder p={"sm"} mb={"sm"}>
                <Group justify="space-between">
                  <Text>Lockscreen Notifications</Text>
                  <Switch
                    name="notifications.lock_screen_notification"
                    checked={
                      formik.values.notifications?.lock_screen_notification
                    }
                    onChange={async (e) => {
                      const updated = {
                        ...formik.values.notifications,
                        lock_screen_notification: e.currentTarget.checked,
                      };
                      formik.setFieldValue("notifications", updated);
                      await updateUserPreferences({
                        data: {
                          ...formik.values,
                          notifications: updated,
                        },
                      });
                    }}
                  />
                </Group>
              </Card>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Card
        withBorder
        p="sm"
        mt="sm"
        bg={
          colorScheme === "dark"
            ? theme.colors.neutral[9]
            : theme.colors.neutral[0]
        }
      >
        <Group justify="space-between">
          <Group>
            <Translate size={20} />
            <Text>Language</Text>
          </Group>
          <Text
            size="sm"
            c={
              colorScheme === "dark"
                ? theme.colors.blue[5]
                : theme.colors.indigo[9]
            }
          >
            {formik.values.language ?? ""}
          </Text>
        </Group>
      </Card>

      <Card
        withBorder
        p="sm"
        mt="sm"
        bg={
          colorScheme === "dark"
            ? theme.colors.neutral[9]
            : theme.colors.neutral[0]
        }
      >
        <Group justify="space-between">
          <Group>
            <Sun size={20} />
            <Text> Dark Mode</Text>
          </Group>
          <Switch
            name="dark_theme"
            color={
              colorScheme === "dark"
                ? theme.colors.blue[6]
                : theme.colors.indigo[9]
            }
            checked={colorScheme === "dark"}
            onChange={async (val) => {
              const newDarkTheme = val.currentTarget.checked;
              // Toggle the Mantine color scheme
              toggleColorScheme();
              // Update formik state
              formik.setFieldValue("dark_theme", newDarkTheme);
              // Update backend preferences
              await updateUserPreferences({
                data: {
                  ...formik.values,
                  dark_theme: newDarkTheme,
                },
              });
            }}
          />
        </Group>
      </Card>
    </Card>
  );
}
